import { useMutation } from '@apollo/client/react'
import Router from 'next/router'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { setCookie } from 'cookies-next'
import { useSnackbars } from 'settle-ui/hooks/useSnackbars/useSnackbars'
import { logEvent } from 'lib/tracking/segment'
import { RouterPage } from 'lib/navigation/consts'
import { payerInvitationRoutes } from 'lib/navigation/helpers/getPayerSignUpPath/getPayerSignUpPath'
import { cookieKeys } from 'lib/consts/browserStorage'
import { getSource } from 'routes/sign-up/payers/helpers/getSource/getSource'
import { useUpdatePayerInvitation } from 'routes/sign-up/payers/hooks/useUpdatePayerInvitation/useUpdatePayerInvitation'
import { pushGtmDataLayerVariables } from 'lib/helpers/pushGtmDataLayerVariables/pushGtmDataLayerVariables'
import { isApolloErrorWithCode } from 'lib/helpers/isApolloErrorWithCode/isApolloErrorWithCode'
import * as events from '../../PayersGetStartedPage.events'
import { type PayersGetStartedPageFormValues } from '../../PayersGetStartedPage'
import { PayersGetStartedFormSubmit_createPayerInvitationDocument } from './usePayersGetStartedFormSubmit.graphql'
import { BusinessTypeEnum, SignupInterestsEnum } from 'gql/types'

export interface UsePayersGetStartedFormSubmitParams {
  invitationCode: string | null
  partner_source: string | null
  utm_source: string | null
  utm_campaign: string | null
  source_page: string | null
}

const COOKIE_MAX_AGE = 60 * 60 * 24 // 24 hours

export const usePayersGetStartedFormSubmit = ({
  invitationCode,
  partner_source,
  utm_source,
  utm_campaign,
  source_page,
}: UsePayersGetStartedFormSubmitParams) => {
  const form = useFormContext<PayersGetStartedPageFormValues>()
  const [loading, setLoading] = useState(false)
  const { showApolloOrTextError } = useSnackbars()
  const [createInvitation] = useMutation(PayersGetStartedFormSubmit_createPayerInvitationDocument)

  const navigateToCreateAccount = (code: string) => {
    setCookie(cookieKeys.simplifiedWcOnboarding, 'true', { maxAge: COOKIE_MAX_AGE, path: '/' })

    void Router.push({
      pathname: payerInvitationRoutes.createAccount,
      query: {
        ...Router.query,
        invitation_code: code,
      },
    })
  }

  const { updatePayerInvitation, loading: updateLoading } = useUpdatePayerInvitation({
    onSuccess: () => {
      if (invitationCode) {
        navigateToCreateAccount(invitationCode)
      }
    },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      pushGtmDataLayerVariables({ email: values.email, firstname: values.firstName, lastname: values.lastName })

      const interestedIn = [SignupInterestsEnum.WORKING_CAPITAL]
      const businessType = BusinessTypeEnum.PAYER
      const source = getSource({ utm_source, utm_campaign, source_page, referring_url: document.referrer })

      if (invitationCode) {
        await updatePayerInvitation({
          invitationCode,
          interestedIn,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          businessName: values.businessName,
          website: values.website,
          businessIndustry: values.businessIndustry,
          prohibitedIndustryConfirmation: values.prohibitedIndustryConfirmation,
          businessType,
          source,
        })
      } else {
        setLoading(true)
        const { data } = await createInvitation({
          variables: {
            email: values.email,
            interestedIn,
            businessType,
            ...(partner_source && { partnerSource: partner_source }),
            source,
          },
        })

        const newInvitationCode = data?.createPayerInvitation?.invitationCode

        if (newInvitationCode) {
          await updatePayerInvitation({
            invitationCode: newInvitationCode,
            interestedIn,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            businessName: values.businessName,
            website: values.website,
            businessIndustry: values.businessIndustry,
            prohibitedIndustryConfirmation: values.prohibitedIndustryConfirmation,
            businessType,
            source,
          })

          navigateToCreateAccount(newInvitationCode)
        }
      }

      logEvent(events.SUBMIT, { partnerSource: partner_source })
    } catch (err) {
      logEvent(events.SUBMIT_ERROR)

      if (isApolloErrorWithCode(err, { code: 'payer_onboarding_email_exists' })) {
        logEvent(events.REDIRECT_TO_LOGIN)
        void Router.push({
          pathname: RouterPage.login,
          query: { email: form.getValues('email') },
        })
      }

      showApolloOrTextError(err)
    } finally {
      setLoading(false)
    }
  })

  return {
    handleSubmit,
    loading: loading || updateLoading,
  }
}
